import clsx from 'clsx'

import { useMemo, useState } from 'preact/hooks'
import { sizeRanges, getSizeType } from '../helpers/universalSizeMapping'
import { getRelatedFilterKeys } from './FilterUtils'
import { sizeUnitLabel } from 'helpers/size-units.mjs'
import { useFilters } from '../hooks/useFilters'
import {
  ageGroupFromEuValue,
  resolveSizeAgeGroup,
  getAgeGroupLabel,
  handleGetTypeValue,
  sizeGroups,
  sizeTypes,
  sortAgeGroupCategories
} from 'snippets/product/product-selector-utils'

const FILTER_SIZE_TYPES = sizeTypes.map((type) => ({
  ...type,
  label: sizeUnitLabel(type.value)
}))

const SIZE_TYPE_ORDER = FILTER_SIZE_TYPES.map((type) => type.value)

function fieldToSizeType(fieldType) {
  if (fieldType === 'EU') return 'EURO'
  return fieldType
}

function findRangeForValue(label, sizeType) {
  const text = String(label ?? '').trim()
  const key = sizeType === 'EURO' ? 'EU' : sizeType
  if (!text || !key) return null
  for (const ranges of Object.values(sizeRanges)) {
    const match = ranges.find((range) => range[key] === text)
    if (match) return match
  }
  return null
}

function euFromCentimetre(label) {
  const cm = parseFloat(label)
  if (Number.isNaN(cm)) return ''
  const footCm = cm >= 100 ? cm / 10 : cm
  return footCm * 1.5
}

function ageGroupFromPackLabel(label, fieldType) {
  const match = String(label ?? '').trim().match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/)
  if (!match) return ''

  const max = parseFloat(match[2])
  if (!Number.isNaN(max) && max >= 50) {
    if (max < 80) return ageGroupFromEuValue(17)
    if (max < 98) return ageGroupFromEuValue(22)
    if (max <= 128) return ageGroupFromEuValue(30)
    return ageGroupFromEuValue(36)
  }

  const start = match[1]
  const chart =
    findRangeForValue(start, fieldType) ||
    findRangeForValue(start, 'UK') ||
    findRangeForValue(start.padStart(2, '0'), 'UK')
  return chart?.EU ? ageGroupFromEuValue(chart.EU) : ''
}

function toSizeOption(label, fieldType, match) {
  const sizeType = fieldToSizeType(fieldType)
  const option = {
    uk: match?.UK || (sizeType === 'UK' ? label : ''),
    us: match?.US || (sizeType === 'US' ? label : ''),
    eu: match?.EU || (sizeType === 'EURO' ? label : ''),
    cm: sizeType === 'CM' ? label : ''
  }

  const eu = option.eu || (option.cm ? euFromCentimetre(option.cm) : '')
  const ageGroup = resolveSizeAgeGroup({ eu, label })
  option.age_group =
    ageGroup !== 'Sizes'
      ? ageGroup
      : ageGroupFromPackLabel(label, fieldType) ||
        (/^\d+(?:\.\d+)?$/.test(label) && sizeType !== 'CM' ? ageGroupFromEuValue(36) : 'Sizes')

  return option
}

function pickCanonicalSources(sources) {
  const uk = sources.filter((source) => source.sizeType === 'UK')
  if (uk.length) return uk
  return sources
}

function unionFilterValues(filter, fetchedFilter) {
  const byKey = new Map()
  for (const item of filter?.values || []) {
    byKey.set(String(item.value ?? item.label), item)
  }
  for (const item of fetchedFilter?.values || []) {
    const key = String(item.value ?? item.label)
    if (!byKey.has(key)) byKey.set(key, item)
  }
  return [...byKey.values()]
}

function sizeGroupsFromChips(chips) {
  const groups = sizeGroups(chips)
  return sortAgeGroupCategories(Object.keys(groups)).map((group) => ({
    key: group,
    label: getAgeGroupLabel(group),
    values: [...(groups[group] || [])].sort((a, b) => customSort(a.label, b.label))
  }))
}

function FilterInputSizingButton({ item, onClick, stacked = false }) {
  const inStock = !item.disabled

  return (
    <button
      type="button"
      onClick={() => (item.alwaysEnabled || inStock) && onClick(item)}
      aria-pressed={item.checked}
      className={clsx(
        'flex relative w-full h-[44px] items-center justify-center paragraph-sm text-center px-1 border border-neutrals-medium-grey rounded-md overflow-hidden transition-all',
        item.checked && inStock && 'text-primary-white bg-primary-blue border-primary-blue',
        item.checked && !inStock && 'text-primary-white bg-neutrals-medium-grey border-neutrals-medium-grey',
        !item.checked && inStock && 'text-primary-black bg-primary-white border-neutrals-medium-grey',
        !item.checked && !inStock && 'text-neutrals-tertiary-grey bg-primary-white border-neutrals-medium-grey'
      )}
    >
      {stacked ? (
        <span className="flex flex-col paragraph-xs leading-tight">
          {String(item.label)
            .split(' ')
            .map((part) => (
              <span key={part}>{part}</span>
            ))}
        </span>
      ) : (
        <span>{item.label}</span>
      )}
      {!inStock && !item.alwaysEnabled && (
        <span
          className={clsx(
            'absolute w-px h-[150%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-[40deg] pointer-events-none z-2 transition-colors',
            item.checked ? 'bg-primary-white' : 'bg-neutrals-medium-grey'
          )}
        />
      )}
    </button>
  )
}

const customSort = (a, b) => {
  const aNon = String(a).toUpperCase() === 'NON'
  const bNon = String(b).toUpperCase() === 'NON'
  if (aNon !== bNon) return aNon ? 1 : -1

  const aYear = /\d\s*-\s*\d+.*Y$/i.test(a)
  const bYear = /\d\s*-\s*\d+.*Y$/i.test(b)
  if (aYear !== bYear) return aYear ? 1 : -1
  if (aYear && bYear) return parseFloat(a) - parseFloat(b)

  if (a.startsWith('0') && !b.startsWith('0')) return -1
  if (!a.startsWith('0') && b.startsWith('0')) return 1
  const aNum = parseFloat(a)
  const bNum = parseFloat(b)
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && aNum !== bNum) return aNum - bNum
  return String(a).localeCompare(String(b), undefined, { numeric: true })
}

export function FilterInputSizing({ initialFilters, activeFilters, fetchedFilters }) {
  const { handleToggleFilterGroup } = useFilters()

  const sizeTypeFilters = useMemo(() => {
    return [...(initialFilters || [])].sort((a, b) => {
      const indexA = SIZE_TYPE_ORDER.indexOf(fieldToSizeType(getSizeType(a.field)))
      const indexB = SIZE_TYPE_ORDER.indexOf(fieldToSizeType(getSizeType(b.field)))
      return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB)
    })
  }, [initialFilters])

  const [selectedSizeType, setSelectedSizeType] = useState(
    fieldToSizeType(getSizeType(sizeTypeFilters[0]?.field)) || 'UK'
  )

  const fetchedByField = useMemo(() => {
    return new Map((fetchedFilters || []).map((filter) => [filter.field, filter]))
  }, [fetchedFilters])

  const values = useMemo(() => {
    const chips = new Map()

    for (const filter of sizeTypeFilters) {
      const fieldType = getSizeType(filter.field)
      const fetchedFilter = fetchedByField.get(filter.field)

      for (const item of unionFilterValues(filter, fetchedFilter)) {
        const raw = item.value ?? item.label
        const label = String(item.label ?? raw ?? '').trim()
        if (!label) continue

        const match = findRangeForValue(label, fieldType)
        const option = toSizeOption(label, fieldType, match)
        const displayLabel = handleGetTypeValue(selectedSizeType, option)
        if (!displayLabel) continue
        const mapKey = `${option.age_group}::${displayLabel}`
        const inFetched = Boolean(
          fetchedFilter?.values?.find((currentValue) => {
            return currentValue.label === item.label || currentValue.value === item.value
          })
        )

        const source = {
          field: filter.field,
          raw,
          sizeType: fieldType,
          value: `${filter.field}:${raw}`,
          inFetched
        }

        const existing = chips.get(mapKey)
        if (!existing) {
          chips.set(mapKey, {
            ...option,
            label: displayLabel,
            sources: [source]
          })
          continue
        }

        if (!existing.sources.some((current) => current.value === source.value)) {
          existing.sources.push(source)
        }
      }
    }

    return [...chips.values()].map((chip) => {
      const sources = pickCanonicalSources(chip.sources)
      const sourceValues = sources.map((source) => source.value)
      const hasFetchedFacets = fetchedByField.size > 0

      return {
        ...chip,
        value: sourceValues.join('|'),
        sourceValues,
        checked: sources.every((source) =>
          getRelatedFilterKeys(source.field, source.raw).some((key) => activeFilters.has(key))
        ),
        disabled: hasFetchedFacets && sources.every((source) => !source.inFetched)
      }
    })
  }, [sizeTypeFilters, selectedSizeType, activeFilters, fetchedByField])

  const groupedValues = useMemo(() => sizeGroupsFromChips(values), [values])

  const handleChipClick = (item) => handleToggleFilterGroup(item.sourceValues)

  if (!sizeTypeFilters.length) return null

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="sticky top-0 z-10 bg-primary-white">
        <div className="flex items-center gap-px w-full px-1 bg-neutrals-light-grey rounded-3xl">
          {FILTER_SIZE_TYPES.map((type) => {
            const id = `size-type-${type.value}`
            return (
              <div className="flex items-center w-full basis-full" key={type.value}>
                <input
                  id={id}
                  type="checkbox"
                  name="size-type"
                  checked={type.value === selectedSizeType}
                  onClick={() => setSelectedSizeType(type.value)}
                  className="hidden field-checkbox peer"
                />
                <label
                  for={id}
                  className="btn btn-solid-white btn-inline btn-sm w-full px-2 !bg-transparent peer-checked:!bg-primary-white"
                >
                  {type.label}
                </label>
              </div>
            )
          })}
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {groupedValues.map((ageGroup) =>
          ageGroup.values.length > 0 ? (
            <div
              key={ageGroup.key}
              className="flex flex-col gap-3 pb-4 border-b last:border-b-0 border-neutrals-medium-grey"
            >
              <p className="paragraph-xs font-bold">{ageGroup.label}</p>
              <div className="grid grid-cols-5 gap-2">
                {ageGroup.values.length > 1 && (
                  <FilterInputSizingButton
                    stacked
                    item={{
                      label: `All ${String(ageGroup.label).trim().split(/\s+/)[0]}`,
                      value: ageGroup.key,
                      sourceValues: ageGroup.values.flatMap((item) => item.sourceValues),
                      checked: ageGroup.values.every((item) => item.checked),
                      disabled: false,
                      alwaysEnabled: true
                    }}
                    onClick={handleChipClick}
                  />
                )}
                {ageGroup.values.map((item) => (
                  <FilterInputSizingButton key={item.value} item={item} onClick={handleChipClick} />
                ))}
              </div>
            </div>
          ) : null
        )}
      </div>
    </div>
  )
}
